import React, {useContext, useEffect, useMemo, useState} from 'react';
import {convertToQuote, getApplication, getUWData, putApplication} from '../../api/applicationAPI';
import {Spin} from "antd";
import {ModelSource} from '../metadata/ModelSource';
import {traverseObject} from '../../utils/objectUtils';
import {ErrorPage} from "../ErrorScope";
import { LoadingOutlined } from '@ant-design/icons';
import {ProductSetup} from "../metadata/api/productSetup";
import { navigate, navigateWithData } from '../../navigation/navigationHistory';
import { PaymentInfoContext } from '../../context/PaymentInfoProvider';
import PaymentInfoProvider from '../../context/PaymentInfoProvider';
import {setQouteOrAppNumber} from '../../api/index';


const antIcon = <LoadingOutlined style={{ fontSize: '38px' }} spin />;

export interface ProductInfo {
  productId: string,
  productSetup: ProductSetup
}

export type Application = any;

export interface ApplicationActionsContextType {

  pullApplication(): Promise<Application>;

  saveApplication(): Promise<Application>;

  convertToQuote(): Promise<Application>;

  resetProductSetup(): any;
}

export const ProductInfoContext = React.createContext<ProductInfo>({
  productId: null,
  productSetup: null
});
export const ApplicationActionsContext = React.createContext<ApplicationActionsContextType>(null);
export const FormEditContext = React.createContext({});

export function ApplicationDataSource({ applicationId, children }) {
  const [state, setState] = useState({
    data: null,
    errors: null,
    locked: false
  });

  const { existCardId } = useContext(PaymentInfoContext);

  let dead = false;
  // @ts-ignore
  useEffect(() => {
    getUWData(applicationId).then(data => {
      if (!dead) {
        setState(state => ({
          ...state, errors:[], data
        }));
      }
    }).catch(onError);

    //TODO:
    return () => (dead = true);
  }, [applicationId]);

  const data = state.data;
  const application = data && data.application;

  useEffect(() => {
    // @ts-ignore
    window.debugApp = () => console.dir(application);
    // @ts-ignore
    window.debugProductInfo = () => console.dir(data.productInfo);
  });

  const deleteExistCardId = app =>{
    const basicPolicy = app.basicPolicy;
    const electronicPaymentSource = {...app.basicPolicy.electronicPaymentSource};
    delete electronicPaymentSource.existCardId;
    app = {...app,basicPolicy:{...basicPolicy,electronicPaymentSource}}
    return app;
  }

  const addExistCardId = app => {
    if (!existCardId){
      return app;
    }
    const basicPolicy = app.basicPolicy;
    const electronicPaymentSource = {...basicPolicy.electronicPaymentSource,existCardId:existCardId};
    app =  {...app,basicPolicy:{...basicPolicy,electronicPaymentSource}};
    return app;
  };

  const resetApplication = application => setState(state => {
    application = addExistCardId(application);
    setQouteOrAppNumber(application.applicationNumber);
    return ({
      ...state,
      errors:[],
      data: {
        ...state.data,
        application
      },
      locked: false
    })
  });

  //only resets the product setup does not  resync the application
  const resetProductSetup = () => {
    return getUWData(applicationId).then(result => {
      if(application.basicPolicy.productVersionIdRef !== result.productInfo.productId){
        const productInfo = result.productInfo;
        setState(state => ({
          ...state,
          errors:[],
          data: {
            ...state.data,
            productInfo,
          },
        }));
      }
    })
  }

  const onError = err => {
    let errors;

    const code = (err.response && err.response.status) || 500;
    const message = (err.response && err.response.data && err.response.data.message) || err.message;
    if (code === 400) {
      errors = [message || 'Invalid form input'];
    } else if (code === 404) {
      errors = [message || 'Application not found'];
    } else if (code === 401) {
      errors = ['Application not found'];
    } else if ((code === 503) || (message === "Network Error")) {
      navigate('/server-maintenance');
    } else {
      //don't expose anything but 400 and 404, we may although expose concurrent modification to bump the update counter later
      errors = ['server is unavailable'];
      console.error(err);
    }

    setState(state => ({...state,
      errors,
      locked: false
    }));

  };

  const setLock = locked => setState(state => ({...state, locked}));

  const actions: ApplicationActionsContextType = useMemo(() => ({
    pullApplication(){
      setLock(true);
      return getApplication(application.systemId)
        .then(resetApplication)
        .catch(onError)
    },

    saveApplication() {
      setLock(true);
      const app = deleteExistCardId(application)
      return putApplication(app)
        .then(resetApplication)
        .catch(onError)
    },

    convertToQuote() {
      setLock(true);
      return convertToQuote(application.systemId)
        .then(getApplication)
        .then(resetApplication)
        .catch(onError)
    },

     resetProductSetup() {
      setLock(true);
      resetProductSetup()
         .catch(onError)
         .finally(() => setLock(false));
    },


  }), [application]);

  const issues = useMemo(() => {
    const all = [];
    const byPath = {};
    application && collectValidationIssues(application, all, byPath);
    state.errors && state.errors.forEach(err => all.push(err));
    return {all, byPath};
  }, [application, state.errors]);


  if (!data || data.application.systemId !== applicationId) {
    return (
      <div
        style={{
          display: 'flex',
          top: '50%',
          left: '50%',
          position: 'absolute'
        }}
      >
        {state.errors&&state.errors.length ? <ErrorPage message={state.errors[0]} /> :  <Spin indicator={antIcon} />}
      </div>
    );
  }

  return <React.Fragment>
    <ProductInfoContext.Provider value={state.data.productInfo}>
      <ApplicationActionsContext.Provider value={actions}>
          <ModelSource data={state.data.application} dataIssues={issues} locked={state.locked}>
            <PaymentInfoProvider>
              {children}
            </PaymentInfoProvider>
          </ModelSource>
      </ApplicationActionsContext.Provider>
    </ProductInfoContext.Provider>
  </React.Fragment>;

}


function collectValidationIssues(app, issues, issuesByPath) {
  traverseObject(app, (obj, path) => {
    if (obj.issues) {
      /*
  Since api call is returning issues as an object contains empty key  ( we dont have parent bean ) for general issues .
 Now  if path is empty we are setting to general so that this step is useful for progressBar and workflow components to display errors.
 */
      if(path===""){
        path="general";
      }
      issuesByPath[path] = [];
      obj.issues.forEach(issue => {
        if (issue.msg) {
          issuesByPath[path].push(issue.msg);
          issues.push(issue.msg);
        }
      });
    }
  });
}
